import React from 'react';
import { format } from 'date-fns';
import { Clock, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import UniversalStatusBadge, { EntityType } from './UniversalStatusBadge';

/**
 * Generic timeline entry structure
 */
export interface TimelineEntry {
  id: string;
  previousStatus: string | null;
  newStatus: string;
  changedBy: string;
  changedAt: string;
  notes?: string | null;
}

interface EntityTimelineProps {
  /** The title for the timeline card */
  title?: string;
  /** Array of timeline entries */
  entries: TimelineEntry[];
  /** Whether the data is loading */
  isLoading?: boolean;
  /** Entity type for status badge rendering */
  entityType: EntityType;
  /** Whether the timeline starts collapsed (default: true) */
  defaultCollapsed?: boolean;
  /** Maximum height of the scrollable content */
  maxHeight?: string;
  /** Custom empty state message */
  emptyMessage?: string;
}

/**
 * EntityTimeline - Reusable timeline component for showing status history
 * 
 * Used to visualize status changes over time for any entity type.
 * Features:
 * - Collapsible by default to save space
 * - Uses UniversalStatusBadge for consistent status styling
 * - Shows change timestamps, user who made the change, and optional notes
 * - Visual timeline with connecting lines and dots
 * 
 * @example
 * <EntityTimeline
 *   title="Status History"
 *   entityType="project"
 *   entries={statusHistory.map(h => ({
 *     id: h.id,
 *     previousStatus: h.previous_status,
 *     newStatus: h.new_status,
 *     changedBy: h.changedByName,
 *     changedAt: h.changed_at,
 *     notes: h.notes
 *   }))}
 *   isLoading={isLoading}
 * />
 */
const EntityTimeline: React.FC<EntityTimelineProps> = ({
  title = 'Status History',
  entries,
  isLoading = false,
  entityType,
  defaultCollapsed = true,
  maxHeight = 'h-64',
  emptyMessage = 'No status changes recorded yet.'
}) => {
  const [isOpen, setIsOpen] = React.useState(!defaultCollapsed);

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3">
                <div className="h-2 w-2 rounded-full bg-muted animate-pulse mt-2" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-24 bg-muted animate-pulse rounded" />
                  <div className="h-5 w-32 bg-muted animate-pulse rounded" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-3 cursor-pointer hover:bg-muted/50 transition-colors">
            <CardTitle className="flex items-center justify-between text-base">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {title}
                {entries.length > 0 && (
                  <span className="text-xs text-muted-foreground font-normal">
                    ({entries.length} {entries.length === 1 ? 'change' : 'changes'})
                  </span>
                )}
              </div>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                {isOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            {entries.length === 0 ? (
              <p className="text-sm text-muted-foreground">{emptyMessage}</p>
            ) : (
              <ScrollArea className={maxHeight}>
                <div className="space-y-4 pr-4">
                  {entries.map((entry) => (
                    <div 
                      key={entry.id} 
                      className="relative pl-6 pb-4 border-l-2 border-muted last:pb-0"
                    >
                      {/* Timeline dot */}
                      <div className="absolute left-[-5px] top-0 h-2 w-2 rounded-full bg-primary" />
                      
                      <div className="space-y-1">
                        {/* Date and time */}
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(entry.changedAt), 'dd MMMM yyyy HH:mm')}
                        </p>
                        
                        {/* Status change */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {entry.previousStatus ? (
                            <>
                              <UniversalStatusBadge 
                                entityType={entityType} 
                                status={entry.previousStatus} 
                                size="sm"
                                showTooltip={false}
                              />
                              <ArrowRight className="h-3 w-3 text-muted-foreground" />
                              <UniversalStatusBadge 
                                entityType={entityType} 
                                status={entry.newStatus} 
                                size="sm"
                                showTooltip={false}
                              />
                            </>
                          ) : (
                            <>
                              <span className="text-sm text-muted-foreground">Initial status:</span>
                              <UniversalStatusBadge 
                                entityType={entityType} 
                                status={entry.newStatus} 
                                size="sm"
                                showTooltip={false}
                              />
                            </>
                          )}
                        </div>
                        
                        {/* Changed by */}
                        <p className="text-xs text-muted-foreground">
                          Changed by {entry.changedBy}
                        </p>
                        
                        {/* Notes */}
                        {entry.notes && (
                          <div className="mt-2 p-2 bg-muted rounded-md">
                            <p className="text-sm text-muted-foreground italic">
                              "{entry.notes}"
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default EntityTimeline;
